/**
 * MealPlanPicker Component
 * Production-ready meal plan picker with accessibility and conflict handling
 */

import React, { useState, useRef, useEffect } from 'react';
import { getDayLabels, getMealLabels, MEAL_PLAN_CONFIG, type MealType } from '../../../config/mealPlan';
import { useMealPlanActions } from '../../../hooks/useMealPlanActions';
import { getWeekContext } from '../../../server/utils/weekContext';
import type { ConflictStatus } from '../../../types/mealPlan';

interface MealPlanPickerProps {
  recipeId: string;
  recipeTitle: string;
  defaultServings?: number;
  userId: string;
  onClose: () => void;
  onSuccess?: () => void;
  className?: string;
}

/**
 * MealPlanPicker
 * Desktop modal for adding recipes to meal plan
 */
export function MealPlanPicker({
  recipeId,
  recipeTitle,
  defaultServings = 2,
  userId,
  onClose,
  onSuccess,
  className = '',
}: MealPlanPickerProps) {
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const [selectedMeal, setSelectedMeal] = useState<MealType>('dinner');
  const [servings, setServings] = useState(defaultServings);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictStatus, setConflictStatus] = useState<ConflictStatus | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLSelectElement>(null);

  const { addRecipe, checkConflict, isAdding } = useMealPlanActions(userId);

  const dayLabels = getDayLabels();
  const mealLabels = getMealLabels();

  // Get current week context
  const { weekId } = getWeekContext(new Date());

  // Focus trap setup
  useEffect(() => {
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    // Check for conflicts first
    const conflict = await checkConflict(weekId, selectedDay, selectedMeal);

    if (conflict.hasConflict) {
      setConflictStatus(conflict);
      setShowConflictDialog(true);
      return;
    }

    // No conflict, proceed with add
    addRecipe(
      {
        recipeId,
        weekId,
        dayIndex: selectedDay,
        mealType: selectedMeal,
        servingsTotal: servings,
        source: 'external',
        conflictStrategy: 'cancel',
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
  };

  const handleReplace = () => {
    addRecipe(
      {
        recipeId,
        weekId,
        dayIndex: selectedDay,
        mealType: selectedMeal,
        servingsTotal: servings,
        source: 'external',
        conflictStrategy: 'replace',
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
    setShowConflictDialog(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="meal-plan-picker-title"
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-card text-card-foreground rounded-lg shadow-xl p-6 ${className}`}
      >
        <h2 id="meal-plan-picker-title" className="text-xl font-semibold mb-4">
          Add to Meal Plan
        </h2>

        <p className="text-gray-600 mb-6">{recipeTitle}</p>

        {/* Day Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2" htmlFor="day-select">
            Select Day
          </label>
          <select
            id="day-select"
            ref={firstFocusableRef}
            value={selectedDay}
            onChange={(e) => setSelectedDay(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {dayLabels.map((day, index) => (
              <option key={index} value={index}>
                {day}
              </option>
            ))}
          </select>
        </div>

        {/* Meal Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Meal Type</label>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(mealLabels) as MealType[]).map((mealType) => (
              <button
                key={mealType}
                type="button"
                onClick={() => setSelectedMeal(mealType)}
                className={`px-4 py-2 rounded-md border transition-colors ${
                  selectedMeal === mealType
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                aria-pressed={selectedMeal === mealType}
              >
                {mealLabels[mealType]}
              </button>
            ))}
          </div>
        </div>

        {/* Servings */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2" htmlFor="servings-input">
            Servings
          </label>
          <input
            id="servings-input"
            type="number"
            min={MEAL_PLAN_CONFIG.MIN_SERVINGS}
            max={MEAL_PLAN_CONFIG.MAX_SERVINGS}
            value={servings}
            onChange={(e) => setServings(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isAdding}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAdding ? 'Adding...' : 'Add to Plan'}
          </button>
        </div>
      </div>

      {/* Conflict Dialog */}
      {showConflictDialog && conflictStatus?.hasConflict && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="conflict-dialog-title"
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-card text-card-foreground rounded-lg shadow-xl p-6"
        >
          <h3 id="conflict-dialog-title" className="text-lg font-semibold mb-3">
            Slot Already Occupied
          </h3>
          <p className="text-gray-600 mb-4">
            {conflictStatus.existingEntry?.recipeTitle} is already scheduled for this slot.
            Do you want to replace it?
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowConflictDialog(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReplace}
              disabled={isAdding}
              className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
            >
              {isAdding ? 'Replacing...' : 'Replace'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
